import { acceptCall, handleIncoming, useCallStore } from '@/state/callStore';
import { CALL_MESSAGE, type CallHandoff } from './swHandlers';

/**
 * A call opened from its push notification (tasks.md T180). The server does
 * not ring a tab that connects after the call started, so the notification
 * hands the call over: to an open tab as a message, or to a new one in the
 * address. It then rings here as usual; "Answer" also accepts it.
 */
export interface CallLink {
  answer: boolean;
  call: CallHandoff;
}

export function readCallLink(search: string): CallLink | null {
  const params = new URLSearchParams(search);
  const callId = params.get('incomingCall');
  const callerId = params.get('callerId');
  if (!callId || !callerId) return null;
  const until = Number(params.get('until'));
  return {
    answer: params.get('answer') === '1',
    call: {
      callId,
      callerId,
      callerName: params.get('callerName') || 'A neighbor',
      callType: params.get('callType') === 'video' ? 'video' : 'audio',
      ...(until > 0 ? { until } : {}),
    },
  };
}

let staleTimer: ReturnType<typeof setTimeout> | undefined;

export function receiveCallFromNotification(link: CallLink): void {
  const call = link?.call;
  if (!call?.callId || !call.callerId) return;
  if (call.until && Date.now() > call.until) return;
  handleIncoming({
    callId: call.callId,
    callerId: call.callerId,
    callerName: call.callerName,
    callType: call.callType,
    callerPhotoUrl: call.callerPhotoUrl || undefined,
  });
  if (call.until) {
    // If the call ended meanwhile this tab would never hear of it: stop ringing when its time is up.
    clearTimeout(staleTimer);
    staleTimer = setTimeout(() => {
      const state = useCallStore.getState();
      if (state.phase === 'incoming-ringing' && state.callId === call.callId) state.reset();
    }, call.until - Date.now());
  }
  if (link.answer) void acceptCall();
}

/** Takes calls from the notification: in the address this tab opened with, and from the worker later. */
export function startCallLinkHandling(): () => void {
  const link = readCallLink(window.location.search);
  if (link) {
    const url = new URL(window.location.href);
    ['incomingCall', 'callerId', 'callerName', 'callType', 'until', 'answer'].forEach((key) =>
      url.searchParams.delete(key),
    );
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    receiveCallFromNotification(link);
  }
  const worker = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
  if (!worker) return () => undefined;
  const onMessage = (event: MessageEvent) => {
    const message = event.data as { type?: string } & CallLink;
    if (message?.type === CALL_MESSAGE) receiveCallFromNotification(message);
  };
  worker.addEventListener('message', onMessage);
  return () => worker.removeEventListener('message', onMessage);
}
